/**
 * 聊天状态管理
 * 从 MemoryService 迁移，适配 Axiom 架构
 */

import { create } from 'zustand'
import { API_BASE_URLS } from '@/shared/services/api'
import type { Message, ExtractionResult } from '../types/memory'

/**
 * 聊天 Store 状态
 */
interface ChatState {
  sessionId: string | null
  messages: Message[]
  isLoading: boolean
  isExtracting: boolean
  extractionResult: ExtractionResult | null
  error: string | null
}

/**
 * 聊天 Store Actions
 */
interface ChatActions {
  sendMessage: (userId: string, projectId: string, content: string) => Promise<void>
  endSession: (userId: string) => Promise<void>
  newSession: () => void
  clearMessages: () => void
  setError: (error: string | null) => void
  setSessionId: (sessionId: string | null) => void
  setMessages: (messages: Message[]) => void
  setExtractionResult: (result: ExtractionResult | null) => void
  setIsExtracting: (isExtracting: boolean) => void
}

/**
 * 聊天 Store
 */
export const useChatStore = create<ChatState & ChatActions>((set, get) => ({
  // 初始状态
  sessionId: null,
  messages: [],
  isLoading: false,
  isExtracting: false,
  extractionResult: null,
  error: null,

  /**
   * 发送消息（流式响应）
   */
  sendMessage: async (userId: string, projectId: string, content: string) => {
    console.log('📨 [ChatStore] sendMessage 被调用:', JSON.stringify({
      userId,
      projectId,
      content,
      contentLength: content.length,
    }, null, 2))
    
    let { sessionId, messages } = get()
    
    // 按照后端规范：session_id 必填，由前端生成 UUID
    // 如果当前没有 sessionId，生成一个新的
    if (!sessionId) {
      sessionId = crypto.randomUUID()
      set({ sessionId })
      console.log('📨 [ChatStore] 生成新 sessionId:', sessionId)
    }
    
    console.log('📨 [ChatStore] 当前状态:', JSON.stringify({
      sessionId,
      messagesCount: messages.length,
    }, null, 2))
    
    // 添加用户消息
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      session_id: sessionId,
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    }
    set({ messages: [...messages, userMessage], isLoading: true, error: null })

    try {
      // 使用 Memory Admin 服务的流式 API
      // 接口：POST /api/v1/chat/stream
      // 按照后端规范：session_id 必填，由前端生成
      const requestUrl = `${API_BASE_URLS.memoryAdmin}/chat/stream`
      const requestBody = {
        user_id: userId,
        project_id: projectId,
        session_id: sessionId,  // 必填，前端生成的 UUID
        message: content,
        stream: true,
      }
      const token = localStorage.getItem('axiom_access_token')
      
      console.log('📤 [ChatStore] 发送请求:', {
        url: requestUrl,
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 20)}...` : null,
        body: requestBody,
      })
      
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      })
      
      console.log('📥 [ChatStore] 响应状态:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
      })

      if (!response.ok) {
        // 尝试解析错误响应体，获取详细错误信息
        let errorMessage = `HTTP error! status: ${response.status}`
        try {
          const errorData = await response.json()
          // 尝试从不同的字段获取错误信息
          const detail = errorData.detail || errorData.message || errorData.error?.message || errorData.error
          if (detail) {
            errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail)
          }
        } catch (e) {
          // 如果无法解析JSON，使用默认错误信息
          console.error('❌ [ChatStore] 无法解析错误响应:', e)
        }
        console.error(`❌ [ChatStore] 发送消息失败:`, {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorMessage,
        })
        throw new Error(errorMessage)
      }

      // 创建助手消息占位
      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        session_id: sessionId,  // sessionId 已确保存在
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
      }
      set((state) => ({ messages: [...state.messages, assistantMessage] }))

      // 处理 SSE 流
      // 注意：按照后端规范，SSE 不再返回 session_id，前端自己管理
      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      let fullContent = ''

      console.log('📥 [ChatStore] 开始读取 SSE 流:', {
        hasReader: !!reader,
        hasBody: !!response.body,
      })

      if (reader) {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            console.log('📥 [ChatStore] SSE 流结束')
            break
          }

          const chunk = decoder.decode(value, { stream: true })
          console.log('📥 [ChatStore] 收到 chunk:', chunk)
          const lines = chunk.split('\n')

          let shouldBreak = false
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6)
              console.log('📥 [ChatStore] SSE data:', data)
              
              // 收到 [DONE] 信号，立即退出循环，不再等待后端关闭连接
              if (data === '[DONE]') {
                console.log('✅ [ChatStore] 收到 [DONE] 信号，主动结束流读取')
                shouldBreak = true
                break
              }

              try {
                const parsed = JSON.parse(data)
                console.log('📥 [ChatStore] 解析后:', JSON.stringify(parsed, null, 2))
                
                // Memory Admin API 格式：
                // - {"type": "start"} - 开始事件
                // - {"content": "..."} - 内容块
                // - {"type": "done"} - 完成事件
                // - {"type": "error", "error": "..."} - 错误事件
                
                // 处理内容块
                if (parsed.content) {
                  console.log('📝 [ChatStore] 收到内容块:', {
                    rawContent: parsed.content,
                    contentType: typeof parsed.content,
                    contentLength: parsed.content.length,
                  })
                  fullContent += parsed.content
                  console.log('📝 [ChatStore] 累积内容:', {
                    fullContent,
                    fullContentLength: fullContent.length,
                  })
                  // 更新消息内容
                  set((state) => ({
                    messages: state.messages.map((msg) =>
                      msg.id === assistantMessage.id
                        ? { ...msg, content: fullContent }
                        : msg
                    ),
                  }))
                } else if (parsed.type === 'error') {
                  throw new Error(parsed.error || '聊天请求失败')
                } else if (parsed.type === 'start') {
                  console.log('🚀 [ChatStore] 收到 start 事件')
                } else if (parsed.type === 'done') {
                  console.log('✅ [ChatStore] 收到 done 事件, 最终内容:', fullContent)
                  // done 事件后通常会跟随 [DONE]，也可以在这里提前结束
                }
              } catch (e) {
                // 如果是错误事件，抛出错误
                if (e instanceof Error) {
                  throw e
                }
                console.warn('⚠️ [ChatStore] JSON 解析失败:', data, e)
              }
            }
          }
          
          // 如果收到 [DONE]，跳出外层 while 循环
          if (shouldBreak) {
            console.log('✅ [ChatStore] 结束 SSE 流读取')
            break
          }
        }
      }

      set({ isLoading: false })
    } catch (error: unknown) {
      // 改进错误处理：显示详细错误信息
      let errorMessage = '发送失败'
      if (error instanceof Error) {
        errorMessage = error.message
        console.error('❌ [ChatStore] 发送消息异常:', {
          message: error.message,
          stack: error.stack,
          error,
        })
      } else {
        console.error('❌ [ChatStore] 发送消息未知错误:', error)
      }
      
      // 根据错误类型提供用户友好的提示
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        errorMessage = '网络连接失败，请检查网络连接或后端服务是否已启动'
      } else if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        errorMessage = '认证失败，请重新登录'
      } else if (errorMessage.includes('500') || errorMessage.includes('Internal Server Error')) {
        errorMessage = `服务器内部错误: ${errorMessage}。请检查后端服务日志或联系管理员`
      }
      
      set({ error: errorMessage, isLoading: false })
    }
  },

  /**
   * 结束会话并触发提取
   * 按照后端规范：使用 Memory Admin API
   * POST /api/v1/chat/sessions/{session_id}/extract
   */
  endSession: async (userId: string) => {
    const { sessionId } = get()
    if (!sessionId) return

    set({ isExtracting: true, error: null })

    try {
      // 使用 Memory Admin API 手动触发提取
      const response = await fetch(`${API_BASE_URLS.memoryAdmin}/chat/sessions/${sessionId}/extract`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('axiom_access_token')}`,
        },
        body: JSON.stringify({
          user_id: userId,
        }),
      })

      if (!response.ok) {
        // 尝试解析错误响应体，获取详细错误信息
        let errorMessage = `HTTP error! status: ${response.status}`
        try {
          const errorData = await response.json()
          // 尝试从不同的字段获取错误信息
          const detail = errorData.detail || errorData.message || errorData.error?.message || errorData.error
          if (detail) {
            errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail)
          }
        } catch (e) {
          // 如果无法解析JSON，使用默认错误信息
          console.error('❌ [ChatStore] 无法解析错误响应:', e)
        }
        console.error(`❌ [ChatStore] 结束会话失败:`, {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorMessage,
        })
        throw new Error(errorMessage)
      }

      const result = await response.json()
      
      // 解析提取结果
      const parseExtractionResult = (data: Record<string, unknown>): ExtractionResult => {
        const extracted = data.extracted as Record<string, unknown> | undefined
        return {
          session_id: (data.session_id as string) || sessionId,
          entities: (extracted?.entities || data.entities || []) as ExtractionResult['entities'],
          relationships: (extracted?.relationships || data.relationships || []) as ExtractionResult['relationships'],
          extracted_at: new Date().toISOString(),
        }
      }
      
      if (result.data?.extraction_status === 'processing') {
        // 异步提取，开始轮询
        // 使用 Memory Admin API 获取提取结果
        // GET /api/v1/chat/sessions/{session_id}/extraction
        const pollStatus = async () => {
          const statusResponse = await fetch(
            `${API_BASE_URLS.memoryAdmin}/chat/sessions/${sessionId}/extraction?user_id=${userId}`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('axiom_access_token')}`,
              },
            }
          )
          const status = await statusResponse.json()
          
          if (status.data?.is_extracting) {
            setTimeout(pollStatus, 1000)
          } else if (status.data?.last_extraction) {
            set({
              extractionResult: parseExtractionResult(status.data.last_extraction),
              isExtracting: false,
              sessionId: null,
            })
          } else {
            set({ isExtracting: false, sessionId: null })
          }
        }
        pollStatus()
      } else {
        set({
          extractionResult: parseExtractionResult(result.data || result),
          isExtracting: false,
          sessionId: null,
        })
      }
    } catch (error: unknown) {
      // 改进错误处理：显示详细错误信息
      let errorMessage = '结束会话失败'
      if (error instanceof Error) {
        errorMessage = error.message
        console.error('❌ [ChatStore] 结束会话异常:', {
          message: error.message,
          stack: error.stack,
          error,
        })
      } else {
        console.error('❌ [ChatStore] 结束会话未知错误:', error)
      }
      
      // 根据错误类型提供用户友好的提示
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        errorMessage = '网络连接失败，请检查网络连接或后端服务是否已启动'
      } else if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        errorMessage = '认证失败，请重新登录'
      } else if (errorMessage.includes('500') || errorMessage.includes('Internal Server Error')) {
        errorMessage = `服务器内部错误: ${errorMessage}。请检查后端服务日志或联系管理员`
      }
      
      set({ error: errorMessage, isExtracting: false })
    }
  },

  /**
   * 创建新会话
   * 立即生成新的 sessionId，与 Agent Admin 保持一致
   */
  newSession: () => {
    const newSessionId = crypto.randomUUID()
    console.log('🆕 [ChatStore] 创建新会话，sessionId:', newSessionId)
    set({
      sessionId: newSessionId,
      messages: [],
      extractionResult: null,
      error: null,
    })
  },

  /**
   * 清空消息并开始新会话
   * 与 Agent Admin 保持一致：清空时生成新的 sessionId
   */
  clearMessages: () => {
    const newSessionId = crypto.randomUUID()
    console.log('🔄 [ChatStore] 清空消息并生成新 sessionId:', newSessionId)
    set({ 
      messages: [],
      sessionId: newSessionId,
      extractionResult: null,
      error: null,
    })
  },

  /**
   * 设置错误
   */
  setError: (error: string | null) => {
    set({ error })
  },

  /**
   * 设置会话 ID
   */
  setSessionId: (sessionId: string | null) => {
    set({ sessionId })
  },

  /**
   * 设置消息列表
   */
  setMessages: (messages: Message[]) => {
    set({ messages, extractionResult: null, error: null })
  },

  /**
   * 设置提取结果
   */
  setExtractionResult: (result: ExtractionResult | null) => {
    set({ extractionResult: result, isExtracting: false })
  },

  /**
   * 设置提取状态
   */
  setIsExtracting: (isExtracting: boolean) => {
    set({ isExtracting })
  },
}))

